// The board: editor, live sync, title bar (save status, who's here, sharing).
import type { BoardInfo, BoardResponse, MeResponse, Person } from '../../shared/api.ts';
import type { KickReason } from '../../shared/protocol.ts';
import { get, goToSignIn, patch, post } from '../api.ts';
import { Editor } from '../editor.ts';
import { attachInput } from '../input.ts';
import { BoardSync, type SyncStatus } from '../sync/board-sync.ts';
import { h } from '../util/dom.ts';
import { createDebugOverlay } from './debug-overlay.ts';
import { getMe, mount, rememberWorkspace, setTitle } from './page.ts';
import { openShareDialog } from './share-dialog.ts';
import { createToolbar, createZoomControls } from './toolbar.ts';
import { createContextBar } from './context-bar.ts';
import { createMinimap } from './minimap.ts';
import { bindShortcutHelp, openShortcutHelp } from './help.ts';
import { menuButton } from './menu.ts';
import { createVersionsPanel } from './versions-panel.ts';
import { icons } from './icons.ts';
import { exportPng, exportSvg } from '../export/export.ts';
import { fileName } from '../util/download.ts';
import { createImageUploader } from '../model/image-upload.ts';
import { canRecord } from '../audio/support.ts';
import type { AudioUploader } from '../editor.ts';
import { bindContextMenu } from './context-menu.ts';
import { keepThumbnailFresh } from '../export/thumbnail.ts';

const MAX_STRESS = 50_000;
const MAX_AVATARS = 5;
const STATUS_TEXT: Record<SyncStatus, string> = {
  connecting: 'Connecting…', saved: 'Saved', saving: 'Saving…', reconnecting: 'Reconnecting… (edits are kept)', closed: 'Disconnected',
};
const KICK_TEXT: Record<KickReason, string> = {
  removed: 'You no longer have access to this board.',
  'access-changed': 'Your access to this board changed. Reloading…',
  'board-deleted': 'This board was deleted, or moved to the trash.',
  unpublished: 'This board is no longer published.',
  busy: 'This board has a lot of viewers right now.',
  'signed-out': 'You were signed out.',
};

export async function boardPage(id: string): Promise<void> {
  const [{ board: initialBoard, workspace: ws }, me] = await Promise.all([
    get<BoardResponse>(`/api/boards/${encodeURIComponent(id)}`),
    getMe(),
  ]);
  let board = initialBoard;
  rememberWorkspace(ws.slug);
  setTitle(board.title);
  document.body.classList.add('board-mode');

  const container = h('main', { id: 'board', 'aria-label': 'Board canvas' });
  const status = h('span', { class: 'save-status', role: 'status', 'data-state': 'connecting' }, [STATUS_TEXT.connecting]);
  const avatars = h('div', { class: 'avatars', 'aria-label': 'People on this board' });
  const banner = h('div', { class: 'banner', role: 'alert', hidden: '' });
  mount(container, titleBar(), banner);

  const editor = new Editor(container, { readOnly: !board.canEdit });
  attachInput(editor);
  // Audio (spec §19): adding files and recording are for editors; anyone can play.
  const audioError = (msg: string) => showBanner(msg, 'Dismiss', () => { banner.hidden = true; });
  editor.audio.onError = audioError;
  if (board.canEdit) {
    // Loaded the first time they're used: most visits never add audio.
    let uploader: Promise<AudioUploader> | null = null;
    editor.audioUploader = (clips, at) => void (uploader ??= import('../model/audio-upload.ts')
      .then((m) => m.createAudioUploader(editor, ws.id, audioError))).then((upload) => upload(clips, at), () => audioError('Couldn’t load the audio tools. Check your connection and try again.'));
    if (canRecord()) {
      editor.recordAudio = () => void import('./recording-bar.ts')
        .then((m) => m.startRecording((clip) => editor.audioUploader?.([clip], null), audioError), () => audioError('Couldn’t load the recorder. Check your connection and try again.'));
    }
  }
  const versions = createVersionsPanel(editor, () => board, (msg) => showBanner(msg, 'Dismiss', () => { banner.hidden = true; }));
  document.body.append(createToolbar(editor), createZoomControls(editor), createMinimap(editor), createContextBar(editor), versions.el, createDebugOverlay(editor));
  bindShortcutHelp(editor);
  const dismissable = (msg: string) => showBanner(msg, 'Dismiss', () => { banner.hidden = true; });
  bindContextMenu(editor, { exportAs, notify: dismissable });
  editor.imageUploader = createImageUploader(editor, ws.id, (msg) => showBanner(msg, 'Dismiss', () => { banner.hidden = true; }));
  // Images and audio pasted from another workspace's board need adopting into this one.
  editor.beforePaste = async (objs) => {
    const hashes = [...new Set(objs.flatMap((o) => (o.type === 'image' || o.type === 'audio' ? [o.assetHash] : [])))];
    if (hashes.length) await post(`/api/workspaces/${ws.id}/assets/adopt`, { hashes });
  };

  // Names for card avatars and the assignee picker (spec §13.3).
  const loadPeople = () => get<Person[]>(`/api/boards/${board.id}/people`).then((list) => editor.people.set(list), () => {});
  editor.people.refresh = () => void loadPeople();
  void loadPeople();

  const params = new URLSearchParams(location.search);
  const sync = new BoardSync(editor, board.id, me.user.id, {
    latencyMs: __DEV__ ? Number(params.get('latency')) || 0 : 0,
    onFirstLoad: () => {
      // ?stress=N adds generated content for profiling (spec §10); &cards=1 makes it all cards.
      const stress = Number(params.get('stress'));
      if (stress > 0 && board.canEdit) editor.addStressObjects(Math.min(stress, MAX_STRESS), params.get('cards') === '1');
      editor.zoomToFit();
    },
    onStatus: (s) => {
      // Viewers never save anything; for them "saved" just means connected.
      status.textContent = !board.canEdit && s === 'saved' ? 'Live' : STATUS_TEXT[s];
      status.dataset.state = s;
    },
    onKicked: (reason) => {
      if (reason === 'access-changed') {
        showBanner(KICK_TEXT[reason]);
        setTimeout(() => location.reload(), 800);
        return;
      }
      // Nothing can be saved any more: stop edits rather than silently dropping them.
      container.classList.add('locked');
      if (reason === 'signed-out') {
        showBanner(KICK_TEXT[reason], 'Sign in', goToSignIn);
      } else {
        showBanner(KICK_TEXT[reason], `Back to ${ws.name}`, () => location.assign(`/w/${ws.slug}`));
      }
    },
    onReject: (reason) => showBanner(`Some changes couldn't be saved: ${reason}`, 'Dismiss', () => { banner.hidden = true; }),
  });
  sync.presence.onPeersChange = renderAvatars;
  editor.syncInfo = () => sync.info;
  window.addEventListener('beforeunload', (e) => {
    if (sync.dirty) e.preventDefault();
  });
  sync.start();
  // Editors keep the board's thumbnail current: board lists and link previews show it (spec §17.3).
  if (board.canEdit) keepThumbnailFresh(editor, board.id, !board.thumbAt || board.thumbAt < board.updatedAt);
  // Explain a view-only board when the plan is why (spec §14.1).
  if (!board.canEdit) {
    const planHref = `/w/${ws.slug}/plan`;
    if (ws.plan === 'team' && ws.billingStatus === 'lapsed') {
      showBanner("This workspace is read-only because its Team plan has ended. You can still view and export this board.",
        ws.role === 'owner' ? 'Choose a plan' : 'See the plan', () => location.assign(planHref));
    } else if (ws.plan === 'free' && ws.role !== 'owner') {
      showBanner('This workspace is on the Free plan, where only its owner edits. You can view this board.');
    }
  }
  if (__DEV__) Object.assign(window as object, { editor, sync });

  function exportAs(format: 'png' | 'svg'): void {
    const name = fileName(board.title) + (editor.selection.size ? ' (selection)' : '');
    (format === 'png' ? exportPng(editor, name) : exportSvg(editor, name))
      .catch((err: unknown) => showBanner(`Export failed: ${(err as Error).message}`, 'Dismiss', () => { banner.hidden = true; }));
  }

  function showBanner(message: string, actionLabel?: string, action?: () => void): void {
    const parts: Node[] = [h('span', {}, [message])];
    if (actionLabel && action) {
      const b = h('button', { type: 'button', class: 'btn small' }, [actionLabel]);
      b.addEventListener('click', action);
      parts.push(b);
    }
    banner.replaceChildren(...parts);
    banner.hidden = false;
  }

  function renderAvatars(): void {
    const peers = sync.presence.list;
    if (peers.some((p) => editor.people.name(p.userId) === null)) void loadPeople(); // someone new
    const shown = peers.slice(0, MAX_AVATARS).map((p) => {
      const el = h('span', { class: 'avatar small', title: p.name, 'aria-label': p.name }, [p.name.slice(0, 1).toUpperCase()]);
      el.style.background = p.color;
      return el;
    });
    if (peers.length > MAX_AVATARS) shown.push(h('span', { class: 'avatar small more' }, [`+${peers.length - MAX_AVATARS}`]));
    avatars.replaceChildren(...shown);
  }

  function titleBar(): HTMLElement {
    const back = h('a', { href: `/w/${ws.slug}`, class: 'tb-btn', title: `Back to ${ws.name}`, 'aria-label': `Back to ${ws.name}` }, ['←']);
    const title = h('input', { class: 'board-title', type: 'text', maxlength: '200', 'aria-label': 'Board title', spellcheck: 'false' });
    title.value = board.title;
    title.readOnly = !board.canEdit;
    const commit = async () => {
      const next = title.value.trim();
      if (!next || next === board.title) {
        title.value = board.title;
        return;
      }
      try {
        board = await patch<BoardInfo>(`/api/boards/${board.id}`, { title: next });
        title.value = board.title;
        setTitle(board.title);
      } catch (err) {
        title.value = board.title;
        alert((err as Error).message);
      }
    };
    title.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') title.blur();
      if (e.key === 'Escape') {
        title.value = board.title;
        title.blur();
      }
    });
    title.addEventListener('blur', () => void commit());

    const parts: Node[] = [back, title, status];
    if (!board.canEdit) parts.push(h('span', { class: 'badge' }, ['View only']));
    parts.push(avatars);
    parts.push(menuButton('More', icons.more, () => {
      const what = editor.selection.size ? 'selection' : 'board';
      return [
        { label: `Export ${what} as PNG`, run: () => exportAs('png') },
        { label: `Export ${what} as SVG`, run: () => exportAs('svg') },
        'separator',
        { label: 'Version history', run: () => versions.open() },
        { label: 'Keyboard shortcuts', hint: '?', run: openShortcutHelp },
        { label: 'Show / hide minimap', hint: 'M', run: () => editor.command('minimap') },
      ];
    }));
    // Taking a published board down stays possible after its workspace goes read-only (spec §17.1).
    if (board.canManage || (board.publicToken && board.canUnpublish)) {
      const share = h('button', { type: 'button', class: 'btn small primary' }, ['Share']);
      share.addEventListener('click', () => openShareDialog(board, ws, (b) => { board = b; }));
      parts.push(share);
    }
    return h('div', { class: 'panel board-bar' }, parts);
  }
}
